import React, { useEffect, useRef, useState } from "react";

// Collapsible wrapper for a search page's filter panel.
//   • Collapses to a slim "Filters" ribbon that can be tapped to reopen.
//   • Controlled mode: pass `collapsed` + `onCollapsedChange` and the parent
//     drives it (e.g. collapse when the results grid scrolls internally).
//   • Uncontrolled mode (no `collapsed` prop): auto-collapses on window scroll.
// `summary` is shown beside the title while collapsed — without it a collapsed
// panel hides which filters are active, which turns "collapse after search"
// from a space win into a way to lose track of what you're looking at.
export default function CollapsibleFilters({ children, title = "Filters", summary, collapsed: collapsedProp, onCollapsedChange }) {
  const controlled = collapsedProp !== undefined;
  const [internal, setInternal] = useState(false);
  const collapsed = controlled ? collapsedProp : internal;
  const lastY = useRef(0);

  const toggle = () => {
    const next = !collapsed;
    if (controlled) { if (onCollapsedChange) onCollapsedChange(next); }
    else setInternal(next);
  };

  useEffect(() => {
    if (controlled) return; // parent drives collapse (e.g. via the results scroll)
    const getY = () =>
      window.scrollY ||
      (document.scrollingElement && document.scrollingElement.scrollTop) ||
      document.documentElement.scrollTop || 0;
    lastY.current = getY();
    const onScroll = () => {
      const y = getY();
      if (y > lastY.current + 4 && y > 180) setInternal(true);
      else if (y < lastY.current - 4 && y < 90) setInternal(false);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", onScroll, { capture: true });
  }, [controlled]);

  return (
    <div className={"sticky-filters" + (collapsed ? " is-collapsed" : "")}>
      <button
        type="button"
        className="sticky-filters-handle"
        onClick={toggle}
        aria-expanded={!collapsed}
      >
        <span>{title}</span>
        {collapsed && summary && (
          <span style={{
            marginLeft: 10, fontWeight: 500, color: "#64748B", textTransform: "none",
            letterSpacing: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{summary}</span>
        )}
        <span className="sticky-filters-caret" aria-hidden="true">{collapsed ? "▾" : "▴"}</span>
      </button>
      <div className="sticky-filters-body" hidden={collapsed}>
        {children}
      </div>
    </div>
  );
}
